/**
 * PieceSideColumn — the Piece screen's side column (FEAT-087 / S-367).
 *
 * **The column is an instrument, not a destination.** It does two things:
 * it changes what the main window is showing, and it carries just enough
 * status that a person who can see everything went fine never has to open a
 * report to confirm it. Anything longer than a status line is a document,
 * and documents open in the main window.
 *
 * An earlier pass loaded this strip with eight expandable panels — SEO,
 * Published, Tokens, Amplifications and the rest — which made a 344px column
 * carry the whole app. The blocks below are the decompressed version:
 *
 *   1. Pipeline           where the piece is and what is coming, with the
 *                         "Show detail" disclosure the design specifies
 *   2. What the agents said   the per-piece diagnosis (placeholder — S-380)
 *   3. Tone & style       the score
 *   4. How it got here    every artifact, grouped; opening one steers the
 *                         main window
 *   5. Piece detail       cards that open composed documents
 *   6. Amplifications
 *   7. Blocked sources
 */

import { useMemo, useState } from "react";
import type {
  OpenDoc,
  PieceDetail,
  PieceDraft,
  PieceImage,
  PieceReport,
  PieceSystemDoc,
} from "@/types/piece";
import type { PipelinePhase } from "@shared/fiction-phases";
import {
  donePhaseCount,
  holdingPhase,
  statusForPhase,
  PHASE_STATUS_LABEL,
} from "@/lib/pipeline-status";
import { snappedStageColor } from "@/lib/stage-color";
import { stageSummary, type StageFacts } from "@/lib/stage-summary";
import {
  TRAIL_GROUPS,
  TRAIL_GROUP_LABEL,
  trailGroupFor,
  humanisePageType,
} from "@/lib/piece-trail";
import { pieceStateLabel } from "@/lib/ui-state";
import { queueReasonFor, stoppedByAgent } from "@/lib/queue-groups";
import { useRole } from "@/lib/useRole";
import { unscoredVoiceState } from "@/lib/voice-score-action";
import { ScoreVoiceButton } from "./ScoreVoiceButton";
import { SideSection } from "./SideSection";
import { ImagesPanel } from "./panels/ImagesPanel";
import { BlockedSourcesPanel } from "./panels/BlockedSourcesPanel";
import { OffspringPanel } from "./panels/OffspringPanel";
// FEAT-084 / S-355 — the thresholds are the orchestrator's own, imported
// rather than restated: the gate decides, this draws the line it decided by.
import {
  voiceDimensions,
  voiceAggregate,
  voiceGateState,
  VOICE_AGGREGATE_GATE,
  VOICE_AGGREGATE_MAX,
  VOICE_FLOOR_PERCENT,
  VOICE_AGGREGATE_GATE_PERCENT,
  VOICE_CATEGORY_FLOOR,
} from "@shared/voice-scores";

/** Which agent produced a draft, from the row's page_type. */
const DRAFT_AGENT: Record<string, string> = {
  draft: "Writer",
  corrected_draft: "Editor",
  seo_draft: "SEO Optimizer",
  outline: "Librarian",
  abstract: "Writer",
};

/**
 * The quality gate the pipeline actually enforces
 * (ContentPipelineOrchestrator.QUALITY_GATE_THRESHOLD): below this the run
 * stops the piece for review. The design draws its gate at 85, but that 85
 * belongs to FEAT-084's four voice sub-scores, which do not exist yet —
 * drawing a line here at 85 would mark a threshold nothing enforces.
 */
const SCORE_GATE = 70;

function wordCount(content: string | null | undefined): number {
  return content ? content.trim().split(/\s+/).length : 0;
}

/** A row in "How it got here". Kind decides what clicking it does. */
type TrailItem =
  | { kind: "report" | "system_doc"; id: number; label: string; sub: string }
  | { kind: "draft"; id: number; label: string; sub: string; isCurrent: boolean };

export interface PieceSideColumnProps {
  piece: PieceDetail;
  reports: PieceReport[];
  systemDocs: PieceSystemDoc[];
  drafts: PieceDraft[];
  images: PieceImage[];
  phases: readonly PipelinePhase[];
  isFiction: boolean;
  openDoc: OpenDoc;
  onOpenDoc: (doc: OpenDoc) => void;
  openGroup: string | null;
  onToggleGroup: (key: string | null) => void;
  /** null = the current draft; an id = that older version, previewed. */
  previewDraftId: number | null;
  onSelectDraft: (id: number | null) => void;
  /** Publication date, already formatted to the reader's date setting. */
  publishedLabel: string | null;
}

export function PieceSideColumn({
  piece,
  reports,
  systemDocs,
  drafts,
  images,
  phases,
  isFiction,
  openDoc,
  onOpenDoc,
  openGroup,
  onToggleGroup,
  previewDraftId,
  onSelectDraft,
  publishedLabel,
}: PieceSideColumnProps) {
  const [pipelineOpen, setPipelineOpen] = useState(false);

  const currentPhase = piece.generation_phases ?? null;
  const doneCount = donePhaseCount(phases, currentPhase);
  const holding = holdingPhase(phases, currentPhase);

  // Newest first, so v4 leads — the order the design's Drafts group shows.
  const sortedDrafts = useMemo(
    () =>
      [...drafts].sort((a, b) =>
        (b.date_created || "").localeCompare(a.date_created || "")
      ),
    [drafts]
  );

  const facts: StageFacts = useMemo(() => {
    const pageTypes = new Set([
      ...reports.map((r) => r.page_type),
      ...systemDocs.map((d) => d.page_type),
    ]);
    const referenceCards = systemDocs.filter(
      (d) => d.page_type === "reference_card"
    ).length;
    const versions = sortedDrafts
      .map((d) => d.version)
      .filter((v): v is number => typeof v === "number");
    return {
      referenceCards,
      latestDraftVersion: versions.length ? Math.max(...versions) : null,
      imageCount: images.length,
      qualityScore: piece.quality_score,
      // FEAT-084 — the number the voice gate reads, so the stage chip shows
      // the same total the gate did.
      voiceAggregate: voiceAggregate(piece),
      seoScore: piece.seo_score,
      hasResearchReport: pageTypes.has("research_report"),
      hasFactCheck:
        pageTypes.has("fact_check_report") || pageTypes.has("continuity_report"),
      publishedLabel,
      status: piece.status,
    };
  }, [reports, systemDocs, sortedDrafts, images.length, piece, publishedLabel]);

  const groups = useMemo(() => {
    const byGroup = new Map<string, TrailItem[]>();
    const add = (key: string, item: TrailItem) => {
      const bucket = byGroup.get(key);
      if (bucket) bucket.push(item);
      else byGroup.set(key, [item]);
    };

    for (const r of reports) {
      add(trailGroupFor(r.page_type), {
        kind: "report",
        id: r.id,
        label: r.title || humanisePageType(r.page_type),
        sub: humanisePageType(r.page_type),
      });
    }
    for (const d of systemDocs) {
      add(trailGroupFor(d.page_type), {
        kind: "system_doc",
        id: d.id,
        label: d.title || humanisePageType(d.page_type),
        sub: humanisePageType(d.page_type),
      });
    }
    sortedDrafts.forEach((d, i) => {
      const agent = DRAFT_AGENT[d.page_type as string] ?? "Pipeline";
      add("drafts", {
        kind: "draft",
        id: d.id,
        label: `Draft v${d.version ?? sortedDrafts.length - i}`,
        sub: `${agent} · ${(d.word_count ?? wordCount(d.content)).toLocaleString()} words · ${
          i === 0 ? "current" : "superseded"
        }`,
        isCurrent: i === 0,
      });
    });

    return TRAIL_GROUPS.filter(
      (g) => byGroup.has(g) || (g === "art" && images.length > 0)
    ).map((key) => ({
      key,
      label: TRAIL_GROUP_LABEL[key],
      items: byGroup.get(key) ?? [],
      imageCount: key === "art" ? images.length : 0,
    }));
  }, [reports, systemDocs, sortedDrafts, images.length]);

  const total = groups.reduce((n, g) => n + g.items.length + g.imageCount, 0);

  /** A group opens on its own while something inside it is on screen. */
  const groupHoldingScreen = useMemo(() => {
    if (openDoc?.kind === "image") return "art";
    for (const g of groups) {
      for (const item of g.items) {
        if (item.kind === "draft") {
          if (previewDraftId != null && item.id === previewDraftId) return g.key;
        } else if (
          openDoc &&
          "id" in openDoc &&
          openDoc.id === item.id &&
          openDoc.kind === item.kind
        ) {
          return g.key;
        }
      }
    }
    return null;
  }, [groups, openDoc, previewDraftId]);

  const score = piece.quality_score;

  return (
    <aside className="piece-side">
      {/* ── 1. Pipeline ──────────────────────────────────────────────── */}
      <SideSection
        title="Pipeline"
        action={{
          label: pipelineOpen ? "Hide detail" : "Show detail",
          onClick: () => setPipelineOpen((v) => !v),
          pressed: pipelineOpen,
        }}
      >
        <div
          className="pside-phases"
          role="img"
          aria-label={`${doneCount} of ${phases.length} phases complete`}
        >
          {phases.map((phase, i) => (
            <span
              key={phase.value}
              className={`pside-phase ${i < doneCount ? "is-done" : ""} ${
                i === doneCount ? "is-current" : ""
              }`}
              // Each stage carries its own stop on the ramp, so the dots in
              // the detail below can match the segment above them.
              style={
                i <= doneCount
                  ? { background: snappedStageColor(i, phases.length) }
                  : undefined
              }
              title={phase.label}
            />
          ))}
        </div>
        <div className="pside-phase-row">
          <span className="pside-phase-label">
            {holding ? `${holding.label} · ` : ""}
            {pieceStateLabel(piece).toLowerCase()}
          </span>
          <span className="pside-phase-count">
            {doneCount} of {phases.length}
          </span>
        </div>

        {pipelineOpen && (
          <ol className="pside-agents">
            <li className="pside-agents-caption">
              {isFiction ? "Fiction pipeline" : "Main pipeline"}
            </li>
            {phases.map((phase, i) => {
              const status = statusForPhase(phase.value, phases, piece);
              const isHere = i === doneCount;
              const colour = snappedStageColor(i, phases.length);
              return (
                <li
                  key={phase.value}
                  className={`pside-agent is-${status} ${isHere ? "is-here" : ""}`}
                >
                  <span
                    className="pside-agent-dot"
                    style={
                      status === "pending" && !isHere
                        ? undefined
                        : { background: colour, borderColor: colour }
                    }
                    aria-hidden
                  />
                  <span className="pside-agent-name">{phase.label}</span>
                  <span className="pside-agent-state">
                    {/* What the stage produced — but only once it has run.
                        A piece can carry artifacts from a stage the pipeline
                        has not reached on this pass (re-runs, send-backs,
                        or a generation_phases that lags reality), and a
                        Pending row claiming "3 images" reads as a result it
                        did not produce. Anything not done reports status. */}
                    {(status === "done" || status === "running"
                      ? stageSummary(phase.value, facts)
                      : null) ?? PHASE_STATUS_LABEL[status]}
                  </span>
                </li>
              );
            })}
          </ol>
        )}

        {piece.revision_notes && piece.ui_state === "revision_requested" && (
          <div className="pside-revision">
            <strong>Revision requested → {piece.revision_target ?? "writer"}</strong>
            {piece.revision_notes}
          </div>
        )}
      </SideSection>

      {/* ── 2. What the agents said ──────────────────────────────────── */}
      {/* S-380 — the same per-piece diagnosis the Queue row carries, from the
          same two columns. One source, two surfaces: the story called for
          exactly that, and computing it twice is how the two would drift. */}
      <SideSection title="What the agents said">
        <AgentVerdict piece={piece} />
      </SideSection>

      {/* ── 3. Voice & style ─────────────────────────────────────────────
          One section, not two. These are both measurements of the same thing
          — does this read as the publication — taken at different moments by
          different agents. Shown as separate sections they read as two
          competing verdicts on the piece. */}
      {/* One overall score for now. The design's four sub-scores — voice
          match, rhythm, structure, vocabulary — plus the summary sentence
          are FEAT-084 (S-352..S-355) and land later. */}
      <SideSection title="Voice & style">
        {score == null ? (
          <p className="pside-empty">
            Not scored yet — the Auditor sets this when it runs.
          </p>
        ) : (
          <>
            <div className="pside-score-row">
              <span className="pside-score">{score}</span>
              <span className="pside-score-max">/100</span>
              <span className="pside-score-gate">gate is {SCORE_GATE}</span>
            </div>
            <div className="pside-score-bar">
              <span
                className={score >= SCORE_GATE ? "is-pass" : "is-under"}
                style={{ width: `${Math.max(0, Math.min(100, score))}%` }}
              />
              <span
                className="pside-score-tick"
                style={{ left: `${SCORE_GATE}%` }}
                aria-hidden
              />
            </div>
            {/* The Auditor's own tone & style line, out of ten. Shown
                alongside rather than folded into the overall number. */}
            {piece.tone_style_score != null && (
              <p className="pside-score-note">
                Tone &amp; style {piece.tone_style_score}/10
              </p>
            )}
          </>
        )}
        <VoiceFidelity
          piece={piece}
          edited={drafts.some((d) => d.page_type === "corrected_draft")}
          onOpenDoc={onOpenDoc}
        />
      </SideSection>

      {/* ── 4. How it got here ───────────────────────────────────────── */}
      <SideSection
        title="How it got here"
        count={total}
        lede="Every document the pipeline produced on the way to this draft, kept in full. Open one to read it in place of the draft."
      >
        {total === 0 ? (
          <p className="pside-empty">
            Nothing yet — each agent's document appears here as it runs.
          </p>
        ) : (
          groups.map((group) => {
            const isOpen =
              openGroup === group.key || groupHoldingScreen === group.key;
            const count = group.items.length + group.imageCount;
            return (
              <div key={group.key} className="trail-group">
                <button
                  type="button"
                  className="trail-group-head"
                  aria-expanded={isOpen}
                  onClick={() => onToggleGroup(isOpen ? null : group.key)}
                >
                  <span className={`trail-caret ${isOpen ? "is-open" : ""}`} aria-hidden>
                    ›
                  </span>
                  <span className="trail-group-label">{group.label}</span>
                  <span className="trail-rule" />
                  <span className="trail-group-count">{count}</span>
                </button>

                {isOpen && (
                  <div className="trail-docs">
                    {group.items.map((item) => {
                      const isReading =
                        item.kind === "draft"
                          ? item.isCurrent
                            ? previewDraftId == null
                            : previewDraftId === item.id
                          : !!openDoc &&
                            "id" in openDoc &&
                            openDoc.id === item.id &&
                            openDoc.kind === item.kind;
                      return (
                        <button
                          key={`${item.kind}-${item.id}`}
                          type="button"
                          className={`trail-doc ${isReading ? "is-reading" : ""}`}
                          onClick={() => {
                            if (item.kind === "draft") {
                              onSelectDraft(item.isCurrent ? null : item.id);
                            } else {
                              onOpenDoc(
                                isReading ? null : { kind: item.kind, id: item.id }
                              );
                            }
                          }}
                        >
                          <span className="trail-doc-text">
                            <span className="trail-doc-label">{item.label}</span>
                            <span className="trail-doc-sub">{item.sub}</span>
                          </span>
                          {isReading && <span className="trail-doc-mark">reading</span>}
                        </button>
                      );
                    })}
                    {group.key === "art" && (
                      <div className="trail-images">
                        {/* Keeps the upload target and the featured/social
                            marks; clicking a thumbnail opens the image at
                            full size in the main window. */}
                        <ImagesPanel
                          images={images}
                          pieceId={piece.id}
                          onOpenImage={(id) =>
                            onOpenDoc(
                              openDoc?.kind === "image" && openDoc.id === id
                                ? null
                                : { kind: "image", id }
                            )
                          }
                          openImageId={
                            openDoc?.kind === "image" ? openDoc.id : null
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </SideSection>

      {/* ── 5. Piece detail ──────────────────────────────────────────── */}
      <SideSection title="Piece detail">
        <button
          type="button"
          className={`pside-card ${openDoc?.kind === "summary" ? "is-reading" : ""}`}
          onClick={() =>
            onOpenDoc(openDoc?.kind === "summary" ? null : { kind: "summary" })
          }
        >
          <span className="pside-card-text">
            <span className="pside-card-label">Piece summary</span>
            <span className="pside-card-sub">
              Characteristics, the brief, publication and token spend
            </span>
          </span>
          {openDoc?.kind === "summary" && (
            <span className="trail-doc-mark">reading</span>
          )}
        </button>
      </SideSection>

      {/* ── 6. Amplifications ────────────────────────────────────────────
          FEAT-092 — replaces the drawer entirely, no summary kept. Every
          draft and posted amplification, with full actions, opens in the
          main window; this is just the button. */}
      <SideSection title="Amplifications">
        <button
          type="button"
          className={`pside-card ${openDoc?.kind === "amplifications" ? "is-reading" : ""}`}
          onClick={() =>
            onOpenDoc(openDoc?.kind === "amplifications" ? null : { kind: "amplifications" })
          }
        >
          <span className="pside-card-text">
            <span className="pside-card-label">Amplifications</span>
            <span className="pside-card-sub">
              Every draft and posted post for this piece
            </span>
          </span>
          {openDoc?.kind === "amplifications" && (
            <span className="trail-doc-mark">reading</span>
          )}
        </button>
      </SideSection>

      {/* ── 7. Offspring ──────────────────────────────────────────────── */}
      <SideSection title="Offspring">
        <OffspringPanel pieceId={piece.id} />
      </SideSection>

      <SideSection title="Blocked sources">
        <BlockedSourcesPanel pieceId={piece.id} />
      </SideSection>
    </aside>
  );
}

/**
 * FEAT-084 / S-355 — voice fidelity.
 *
 * Four dimensions scored against the publication's voice profile *after* the
 * Editor finished, plus the Auditor's own observation from before it. The two
 * together answer a question nothing used to: did the Editor's corrections
 * preserve the voice the Auditor asked it to preserve?
 *
 * Renders nothing at all when the evaluator has not run. An unscored piece is
 * not a zero-scored piece, and four empty bars would read as a catastrophe.
 *
 * BUG-132 — the one exception: an edited piece the evaluator never scored gets
 * a line and the Score voice button here. The button first shipped only inside
 * the voice report, which this column links to only when there are notes to
 * read — so for the pieces that needed it, it could not be reached.
 */
function VoiceFidelity({
  piece,
  edited,
  onOpenDoc,
}: {
  piece: PieceDetail;
  /** Whether the piece has a corrected draft — the Editor has run on it. */
  edited: boolean;
  onOpenDoc: (doc: OpenDoc) => void;
}) {
  const { canEdit } = useRole();
  const dims = voiceDimensions(piece);
  const total = voiceAggregate(piece);
  const state = voiceGateState(piece);
  const hasProse = Boolean(piece.voice_evaluator_notes || piece.voice_notes);
  const unscored = unscoredVoiceState({
    hasScoresOrNotes: total != null || !!piece.voice_evaluator_notes,
    edited,
  });

  if (state === "unscored" && !hasProse && unscored !== "unscored") return null;

  return (
    <div className="pside-voice">
      <p className="pside-voice-label-row">Voice fidelity</p>

      {unscored === "unscored" && (
        <>
          <p className="pside-empty">Not scored yet.</p>
          {canEdit && <ScoreVoiceButton pieceId={piece.id} />}
        </>
      )}

      {state !== "unscored" && (
        <>
          {total != null && (
            <>
              <div className="pside-voice-total">
                <span className="pside-voice-total-n">{total}</span>
                <span className="pside-voice-total-max">/{VOICE_AGGREGATE_MAX}</span>
                <span className="pside-voice-total-gate">gate {VOICE_AGGREGATE_GATE}</span>
              </div>
              <span className="pside-voice-bar pside-voice-bar-total">
                <span
                  className={state === "pass" ? "is-pass" : "is-under"}
                  style={{ width: `${(total / VOICE_AGGREGATE_MAX) * 100}%` }}
                />
                <span
                  className="pside-voice-tick"
                  style={{ left: `${VOICE_AGGREGATE_GATE_PERCENT}%` }}
                  aria-hidden
                />
              </span>
            </>
          )}

          <div className="pside-voice-dims">
            {dims.map((d) => (
              <div key={d.key} className="pside-voice-dim">
                <span className="pside-voice-label">{d.label}</span>
                <span className="pside-voice-bar">
                  <span
                    className={d.under ? "is-under" : "is-pass"}
                    style={{ width: `${d.percent}%` }}
                  />
                  {/* The floor, from the same constant the gate reads. */}
                  <span
                    className="pside-voice-tick"
                    style={{ left: `${VOICE_FLOOR_PERCENT}%` }}
                    aria-hidden
                  />
                </span>
                <span className="pside-voice-value">
                  {d.score == null ? "\u2014" : d.score}
                </span>
              </div>
            ))}
          </div>

          {state === "under_floor" && (
            <p className="pside-voice-verdict">
              A dimension is under {VOICE_CATEGORY_FLOOR} — weak in one place
              rather than everywhere.
            </p>
          )}
          {state === "under_aggregate" && (
            <p className="pside-voice-verdict">
              Under {VOICE_AGGREGATE_GATE} overall.
            </p>
          )}
        </>
      )}

      {/* The prose lives in the main window. Three sentences of dense
          criticism in a 344px strip is a wall — it shipped that way once,
          clamped to four lines, which was the tell. */}
      {hasProse && (
        <button
          type="button"
          className="pside-voice-more"
          onClick={() => onOpenDoc({ kind: "voice" })}
        >
          Read the assessment
        </button>
      )}
    </div>
  );
}

/**
 * S-380 — what the agent that stopped this piece said about why.
 *
 * Reads `queueReasonFor`, the same function the Desk queue's rows use, so the
 * two surfaces cannot disagree about the same piece. The empty state is honest
 * rather than hidden: a piece nothing has stopped has nothing to report, and
 * saying so is better than an absent section the reader cannot distinguish
 * from a broken one.
 */
function AgentVerdict({ piece }: { piece: PieceDetail }) {
  const { pill, line } = queueReasonFor(piece);
  const agent = stoppedByAgent(piece);
  if (!pill && !line) {
    return <p className="pside-empty">Nothing here to report.</p>;
  }
  return (
    <div className="pside-verdict">
      <p className="pside-verdict-pill">
        {agent}
        {pill ? <span className="pside-verdict-sep"> · </span> : null}
        {pill}
      </p>
      {line ? <p className="pside-verdict-line">{line}</p> : null}
    </div>
  );
}